import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormsApiService, FormSubmissionsExportService } from '@vendasta/forms_microservice';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map, take, withLatestFrom } from 'rxjs/operators';

function downloadFile(file: Blob, filename: string): void {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.setAttribute('style', 'display:none;');
  document.body.appendChild(link);
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

@Injectable({ providedIn: 'root' })
export class FormSubmissionsExportCsvService {
  private csvExportStatusMap$$ = new BehaviorSubject<Map<string, boolean>>(new Map());

  constructor(
    private readonly formApiService: FormsApiService,
    private readonly formExportService: FormSubmissionsExportService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
  ) {}

  private setExportCSVStatus(formId: string, status: boolean): void {
    const csvStatusMap = this.csvExportStatusMap$$.value;
    csvStatusMap.set(formId, status);
    this.csvExportStatusMap$$.next(csvStatusMap);
  }

  getCSVExportStatus(formId: string): Observable<boolean> {
    return this.csvExportStatusMap$$.pipe(map((statusMap) => !!statusMap.get(formId)));
  }

  exportCSV(formId: string): void {
    this.setExportCSVStatus(formId, true);
    this.snackbarService.openSuccessSnack('CUSTOM_FORMS.FORM_SUBMISSION_LIST.EXPORT_TO_CSV_MESSAGES.STARTED');
    this.formExportService
      .exportCSV(formId)
      .pipe(
        take(1),
        withLatestFrom(
          this.formApiService.getForm({
            formId: formId,
          }),
        ),
        finalize(() => this.setExportCSVStatus(formId, false)),
      )
      .subscribe(
        ([file, formResponse]) => {
          this.snackbarService.openSuccessSnack(
            this.translateService.instant('CUSTOM_FORMS.FORM_SUBMISSION_LIST.EXPORT_TO_CSV_MESSAGES.SUCCESS', {
              formName: formResponse.formConfig?.name || 'Untitled',
            }),
          );
          downloadFile(file.blob, file.filename);
        },
        (error) => {
          console.error(error);
          this.setExportCSVStatus(formId, false);
          this.snackbarService.openErrorSnack('CUSTOM_FORMS.FORM_SUBMISSION_LIST.EXPORT_TO_CSV_MESSAGES.FAILED');
        },
      );
  }
}
